/* istanbul ignore file */
import logger from '@adc/logger'
import configment from '@adc/configment'

declare global {
  interface Window {
    embeddedservice_bootstrap?: {
      settings: {
        language: string
      }
      init: (
        chatId: string,
        name: string,
        sandboxUrl: string,
        options: { scrt2URL: string }
      ) => void
      prechatAPI?: {
        setHiddenPrechatFields?: (fields: Record<string, string>) => void
        setVisiblePrechatFields?: (fields: Record<string, string>) => void
      }
    }
  }
}

export enum ChatVariants {
  Default = 'default',
  Concierge = 'concierge',
}

export type ChatConfig = {
  scriptInitUrl: string
  sandboxUrl: string
  name: string
  chatId: string
}

export type ChatConfigList = Record<ChatVariants, ChatConfig>

const CHAT_WINDOW_HEIGHT = 505
const CHAT_WINDOW_WIDTH = 485
const SAFARI_HEIGHT_ADJUSTMENT = 18

function openChatPopup(user: any) {
  let url =
    (typeof window === 'undefined' ? '' : window.location.origin) + '/apps/chat'
  if (user?.logged_in) {
    url =
      url +
      '?Email=' +
      user.email_address +
      '&FirstName=' +
      user.first_name +
      '&LastName=' +
      user.last_name
  }

  if (typeof window !== 'undefined') {
    const originUrl = window.location.href
    url += user?.logged_in ? '&' : '?'
    url += 'originUrl=' + encodeURIComponent(originUrl)
  }

  popupCenter(url, CHAT_WINDOW_WIDTH, CHAT_WINDOW_HEIGHT)
}

function popupCenter(url: string, w: number, h: number) {
  // Fixes dual-screen position
  const dualScreenLeft =
    // @ts-ignore next
    window.screenLeft !== undefined ? window.screenLeft : screen.left
  const dualScreenTop =
    // @ts-ignore next
    window.screenTop !== undefined ? window.screenTop : screen.top
  let width
  let height

  if (window.innerWidth) {
    width = window.innerWidth
  } else {
    width = document.documentElement.clientWidth
      ? document.documentElement.clientWidth
      : screen.width
  }

  if (window.innerHeight) {
    height = window.innerHeight
  } else {
    height = document.documentElement.clientHeight
      ? document.documentElement.clientHeight
      : screen.height
  }

  const left = width / 2 - w / 2 + dualScreenLeft
  const top = height / 2 - h / 2 + dualScreenTop

  // safari hack as safari opens windows slightly smaller.
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent)
  const newWindow = window.open(
    url,
    'apps/chat',
    `scrollbars=yes, toolbar=no, titlebar=no, width=${w}, height=${
      h + (isSafari ? SAFARI_HEIGHT_ADJUSTMENT : 0)
    }, top=${top}, left=${left}`
  )

  // Puts focus on the newWindow
  if (newWindow) {
    newWindow.focus()
  }
}

export const setUserChatInfo = (firstName = '', lastName = '', email = '') => {
  window?.embeddedservice_bootstrap?.prechatAPI?.setHiddenPrechatFields?.({
    FName: firstName,
    LName: lastName,
    Email2: email,
    URL: window.location.href,
    Browser: window.navigator.userAgent,
  })
}

/**
 * Sets visible pre-chat fields for logged-in users
 * Uses Salesforce's custom field names: FName, LName, Email2
 */
export const setVisibleUserChatInfo = (
  firstName = '',
  lastName = '',
  email = ''
) => {
  const fields: Record<string, any> = {}

  if (firstName) {
    fields.FName = { value: firstName, isEditableByEndUser: true }
  }
  if (lastName) {
    fields.LName = { value: lastName, isEditableByEndUser: true }
  }
  if (email) {
    fields.Email2 = { value: email, isEditableByEndUser: true }
  }

  if (Object.keys(fields).length > 0) {
    window?.embeddedservice_bootstrap?.prechatAPI?.setVisiblePrechatFields?.(
      fields
    )
  }
}

const initEmbeddedMessaging = (
  onButtonClicked: () => void,
  selectedChatConfig: ChatConfig,
  user?: any
) => {
  // @ts-ignore next
  try {
    if (window.embeddedservice_bootstrap?.settings) {
      window.embeddedservice_bootstrap.settings.language = 'en_US'
    }

    window.addEventListener('onEmbeddedMessagingButtonClicked', () => {
      onButtonClicked()
    })

    // Set fields when pre-chat form loads - this is the correct event!
    window.addEventListener('onEmbeddedMessagingPreChatLoaded', () => {
      if (user?.logged_in) {
        setVisibleUserChatInfo(
          user.first_name,
          user.last_name,
          user.email_address || user.email
        )

        // Retry once after a short delay to ensure form is fully rendered
        setTimeout(() => {
          setVisibleUserChatInfo(
            user.first_name,
            user.last_name,
            user.email_address || user.email
          )
        }, 500)
      }
    })

    window.embeddedservice_bootstrap?.init(
      selectedChatConfig.chatId,
      selectedChatConfig.name,
      selectedChatConfig.sandboxUrl,
      {
        scrt2URL: selectedChatConfig.scriptInitUrl,
      }
    )
  } catch (err) {
    logger.error('Error loading Embedded Messaging: ', err)
  }
}

const loadSalesforceV2Chat = (
  onButtonClicked: () => void,
  variant: ChatVariants = ChatVariants.Default,
  user?: any
) => {
  // @ts-ignore next
  const salesforceChatConfig: ChatConfigList = configment.get(
    'SALESFORCE_V2_CHAT',
    {}
  )
  const selectedChatConfig = salesforceChatConfig[variant]
  if (selectedChatConfig) {
    const script = document.createElement('script')
    script.src = `${selectedChatConfig.sandboxUrl}/assets/js/bootstrap.min.js`
    script.onload = () => {
      initEmbeddedMessaging(onButtonClicked, selectedChatConfig, user)
    }
    document.body.appendChild(script)
  } else {
    console.error('No chat configuration found for variant:', variant)
  }
}

export default {
  openChatPopup,
  loadSalesforceV2Chat,
  setUserChatInfo,
  setVisibleUserChatInfo,
  ChatVariants,
}
